import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/store';
import type { BondCertificate, Companion } from '../lib/types';
import { BondMeter } from '../components/bond/BondMeter';
import { Ribbon } from '../components/ribbons/Ribbon';
import { Button, EmptyState, ErrorState, LoadingGrid, Modal, Panel } from '../components/ui';
import { formatDate, formatNumber } from '../lib/format';

/**
 * The Hall of Companions.
 *
 * The most prestigious page a profile has, and the one that takes the longest
 * to fill — a Companion is roughly a year of ordinary play with one Pokémon, so
 * a Hall with three in it is a statement.
 *
 * It is public, and deliberately: the point of spending a year on something is
 * that somebody else can see you did.
 */

interface Response {
  companions: Companion[];
  trainer?: { userId: string; username: string };
}

export function CompanionsPage() {
  const { username } = useParams();
  const state = useAsync<Response>(
    () => api.get(username ? `/companions/${username}` : '/companions'),
    [username],
  );
  const [certificateFor, setCertificateFor] = useState<string | null>(null);

  if (state.error) return <ErrorState message={state.error} onRetry={state.reload} />;
  if (!state.data) return <LoadingGrid count={3} height={220} />;

  const { companions } = state.data;
  const whose = state.data.trainer ? `${state.data.trainer.username}'s` : 'Your';

  return (
    <div className="stack">
      <div className="page-head">
        <div>
          <h1 className="page-head__title">Hall of Companions</h1>
          <div className="page-head__sub">
            {companions.length === 0
              ? 'Soulbound Pokémon are kept here, permanently.'
              : `${companions.length} Soulbound ${companions.length === 1 ? 'Companion' : 'Companions'}`}
          </div>
        </div>
      </div>

      {companions.length === 0 ? (
        <EmptyState art="♥" title={`${whose} Hall is empty — for now`}>
          A Pokémon becomes Soulbound after a very long time in your care. There is no
          way to hurry it and that is the whole idea: the Hall is a record of patience,
          not of effort.
        </EmptyState>
      ) : (
        <div className="hall">
          {companions.map((companion) => (
            <CompanionCard
              key={companion.pokemonId}
              companion={companion}
              onCertificate={() => setCertificateFor(companion.pokemonId)}
            />
          ))}
        </div>
      )}

      {certificateFor && (
        <CertificateModal pokemonId={certificateFor} onClose={() => setCertificateFor(null)} />
      )}
    </div>
  );
}

function CompanionCard({
  companion,
  onCertificate,
}: {
  companion: Companion;
  onCertificate: () => void;
}) {
  return (
    <article className="companion pxframe">
      <div className="companion__portrait">
        <img src={companion.sprite} alt="" className="pixel" width={96} height={96} />
        {companion.isShiny && <span className="companion__shiny" aria-label="Shiny">✦</span>}
      </div>

      <div className="companion__body stack-sm">
        <div>
          <Link to={`/pokemon/${companion.pokemonId}`} className="companion__name">
            {companion.displayName}
          </Link>
          <div className="tiny muted">
            {companion.species} · Level {companion.level}
          </div>
        </div>

        <BondMeter bond={companion.bond} size="sm" />

        <dl className="companion__stats">
          <Stat label="Hatched" value={companion.hatchedAt ? formatDate(companion.hatchedAt) : '—'} />
          <Stat label="Yours since" value={formatDate(companion.obtainedAt)} />
          <Stat
            label="Together"
            value={
              companion.yearsOwned >= 1
                ? `${companion.yearsOwned} ${companion.yearsOwned === 1 ? 'year' : 'years'}`
                : `${companion.daysOwned} days`
            }
          />
          <Stat label="Fed" value={formatNumber(companion.feedCount)} />
          <Stat label="Eggs" value={formatNumber(companion.eggsProduced)} />
          <Stat label="Ribbons" value={formatNumber(companion.ribbonCount)} />
          <Stat label="Original trainer" value={companion.originalTrainer ?? 'Unknown'} />
        </dl>

        {companion.ribbons.length > 0 && (
          <div className="sash sash--compact">
            {companion.ribbons.map((ribbon) => (
              <Ribbon key={ribbon.ribbonKey} ribbon={ribbon} size={30} />
            ))}
          </div>
        )}

        {companion.memories.length > 0 && (
          <ul className="companion__memories">
            {companion.memories.map((memory) => (
              <li key={`${memory.title}-${memory.createdAt}`}>
                <strong>{memory.title}</strong>
                <span className="tiny muted"> · {formatDate(memory.createdAt)}</span>
              </li>
            ))}
          </ul>
        )}

        <Button size="sm" variant="gold" onClick={onCertificate}>
          View certificate
        </Button>
      </div>
    </article>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="companion__stat">
      <dt className="tiny muted">{label}</dt>
      <dd className="mono">{value}</dd>
    </div>
  );
}

/**
 * The certificate.
 *
 * Rendered as a document rather than as a panel — a border, a seal, a number
 * and a signature line. It is meant to look like something you would print,
 * which is also why every value on it is a plain string rather than a chart:
 * the eventual "download this" is a screenshot of exactly this markup.
 */
function CertificateModal({ pokemonId, onClose }: { pokemonId: string; onClose: () => void }) {
  const state = useAsync<{ certificate: BondCertificate }>(
    () => api.get(`/companions/certificate/${pokemonId}`),
    [pokemonId],
  );

  return (
    <Modal onClose={onClose} labelledBy="certificate-title" wide>
      {state.error && <ErrorState message={state.error} onRetry={state.reload} />}
      {!state.data && !state.error && <Panel raised={false}>Loading…</Panel>}
      {state.data && <Certificate certificate={state.data.certificate} />}
    </Modal>
  );
}

export function Certificate({ certificate }: { certificate: BondCertificate }) {
  const c = certificate;
  return (
    <div className="certificate">
      <div className="certificate__rule" aria-hidden />
      <header className="certificate__head">
        <div className="certificate__eyebrow mono">PokéGrove · Hall of Companions</div>
        <h2 id="certificate-title" className="certificate__title">
          Certificate of Soulbound
        </h2>
        <div className="certificate__no mono">{c.certificateNo}</div>
      </header>

      <div className="certificate__body">
        <div className="certificate__portrait">
          <img src={c.sprite} alt="" className="pixel" width={112} height={112} />
          {c.isShiny && <span className="companion__shiny" aria-label="Shiny">✦</span>}
        </div>

        <div className="certificate__facts">
          <p className="certificate__lede">
            This certifies that <strong>{c.displayName}</strong>
            {c.nickname && <span className="muted"> ({c.species})</span>} and{' '}
            <strong>{c.trainer}</strong> became Soulbound on {formatDate(c.soulboundAt)}.
          </p>

          <dl className="certificate__grid">
            <Fact label="Species" value={c.species} />
            <Fact label="Nickname" value={c.nickname ?? '—'} />
            <Fact label="Original trainer" value={c.originalTrainer} />
            <Fact label="Hatched" value={c.hatchedAt ? formatDate(c.hatchedAt) : 'Not hatched in this Grove'} />
            <Fact label="Together since" value={formatDate(c.obtainedAt)} />
            <Fact
              label="Years together"
              value={c.lifetime.yearsOwned >= 1 ? String(c.lifetime.yearsOwned) : `${c.lifetime.daysOwned} days`}
            />
            <Fact label="Level" value={String(c.lifetime.level)} />
            <Fact label="Times fed" value={formatNumber(c.lifetime.feeds)} />
            <Fact label="Meals from you" value={formatNumber(c.lifetime.selfCares)} />
            <Fact label="Eggs produced" value={formatNumber(c.lifetime.eggsProduced)} />
            <Fact label="Ribbons" value={formatNumber(c.lifetime.ribbons)} />
            <Fact label="Memories kept" value={formatNumber(c.lifetime.memories)} />
          </dl>
        </div>
      </div>

      {c.ribbons.length > 0 && (
        <div className="certificate__ribbons">
          {c.ribbons.map((ribbon) => (
            <Ribbon key={ribbon.ribbonKey} ribbon={ribbon} size={34} />
          ))}
        </div>
      )}

      {c.highlights.length > 0 && (
        <ol className="certificate__timeline">
          {c.highlights.map((memory) => (
            <li key={`${memory.title}-${memory.createdAt}`}>
              <span className="mono tiny">{formatDate(memory.createdAt)}</span>
              <strong>{memory.title}</strong>
              {memory.body && <span className="tiny muted"> — {memory.body}</span>}
            </li>
          ))}
        </ol>
      )}

      <footer className="certificate__foot">
        <div className="certificate__seal" aria-hidden>
          <span>✦</span>
        </div>
        <div className="tiny muted">
          Issued by PokéGrove. This certificate is permanent and its number is never reissued.
        </div>
      </footer>
      <div className="certificate__rule" aria-hidden />
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className="certificate__fact">
      <dt className="tiny muted">{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
